import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

interface LoadingOptions {
  global?: boolean;
  key?: string;
  minDuration?: number;
  delayMs?: number;
}

interface LoadingContextType {
  isLoading: boolean;
  isLoadingByKey: (key: string) => boolean;
  loadingKeys: Set<string>;
  startLoading: (options?: LoadingOptions) => void;
  stopLoading: (options?: LoadingOptions) => void;
  withLoading: <T>(
    asyncFunction: () => Promise<T>,
    options?: LoadingOptions,
  ) => Promise<T>;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

interface LoadingProviderProps {
  children: React.ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({
  children,
}) => {
  const [globalLoadingCount, setGlobalLoadingCount] = useState(0);
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());
  const [showGlobalLoading, setShowGlobalLoading] = useState(false);
  const globalLoadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const startLoading = useCallback((options: LoadingOptions = {}) => {
    const { global = true, key } = options;

    if (global) {
      setGlobalLoadingCount((prev) => prev + 1);
    }

    if (key) {
      setLoadingKeys((prev) => new Set(prev).add(key));
    }
  }, []);

  const stopLoading = useCallback((options: LoadingOptions = {}) => {
    const { global = true, key } = options;

    if (global) {
      setGlobalLoadingCount((prev) => Math.max(0, prev - 1));
    }

    if (key) {
      setLoadingKeys((prev) => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  }, []);

  // 處理全域 loading 的延遲顯示
  useEffect(() => {
    if (globalLoadingCount > 0) {
      // 清除之前的定時器
      if (globalLoadingTimerRef.current) {
        clearTimeout(globalLoadingTimerRef.current);
      }

      // 如果尚未顯示loading，設定延遲顯示
      if (!showGlobalLoading) {
        const timer = setTimeout(() => {
          setShowGlobalLoading(true);
        }, 200); // 200ms延遲，避免短暫操作顯示loading
        globalLoadingTimerRef.current = timer;
      }
    } else {
      // loading結束時立即隱藏
      if (globalLoadingTimerRef.current) {
        clearTimeout(globalLoadingTimerRef.current);
        globalLoadingTimerRef.current = null;
      }
      setShowGlobalLoading(false);
    }

    return () => {
      if (globalLoadingTimerRef.current) {
        clearTimeout(globalLoadingTimerRef.current);
      }
    };
  }, [globalLoadingCount, showGlobalLoading]);

  const withLoading = useCallback(
    async <T,>(
      asyncFunction: () => Promise<T>,
      options: LoadingOptions = {},
    ): Promise<T> => {
      const { minDuration = 0 } = options;
      const startTime = Date.now();

      startLoading(options);

      try {
        const result = await asyncFunction();

        // 確保最小載入時間
        const elapsedTime = Date.now() - startTime;
        if (minDuration > elapsedTime) {
          await new Promise((resolve) =>
            setTimeout(resolve, minDuration - elapsedTime),
          );
        }

        return result;
      } finally {
        stopLoading(options);
      }
    },
    [startLoading, stopLoading],
  );

  const isLoadingByKey = useCallback(
    (key: string) => loadingKeys.has(key),
    [loadingKeys],
  );

  const value: LoadingContextType = {
    isLoading: showGlobalLoading,
    isLoadingByKey,
    loadingKeys,
    startLoading,
    stopLoading,
    withLoading,
  };

  return (
    <LoadingContext.Provider value={value}>{children}</LoadingContext.Provider>
  );
};
